import React, { useEffect, useRef, useState } from "react";
import { Loader2, MapPin } from "lucide-react";
import api from "@/lib/api";

// Autocomplete comune italiano (OpenStreetMap Nominatim, solo citta'/paesi) per
// l'acquisizione privati manuale: mostra solo comuni reali e passa lat/lng/zoom
// al genitore quando l'utente ne seleziona uno, per centrare la mappa.
export default function CityAutocomplete({ value, onChange, onSelect, className, dataTestId, placeholder }) {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const debounceRef = useRef(null);
    const wrapperRef = useRef(null);
    const requestSeqRef = useRef(0);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    const handleInputChange = (text) => {
        onChange(text);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (text.trim().length < 2) {
            setSuggestions([]);
            setOpen(false);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            const seq = ++requestSeqRef.current;
            setLoading(true);
            try {
                const { data } = await api.get("/geocode/city-suggest", { params: { q: text.trim() } });
                if (seq !== requestSeqRef.current) return; // risposta obsoleta, superata da una digitazione più recente
                setSuggestions(data.suggestions || []);
                setOpen((data.suggestions || []).length > 0);
            } catch {
                if (seq === requestSeqRef.current) setSuggestions([]);
            } finally {
                if (seq === requestSeqRef.current) setLoading(false);
            }
        }, 400);
    };

    const pick = (s) => {
        onChange(s.name);
        onSelect(s);
        setSuggestions([]);
        setOpen(false);
    };

    return (
        <div ref={wrapperRef} className="relative">
            <div className="relative">
                <input
                    data-testid={dataTestId}
                    value={value}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onFocus={() => suggestions.length > 0 && setOpen(true)}
                    onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
                    placeholder={placeholder}
                    autoComplete="off"
                    className={className}
                />
                {loading && <Loader2 size={14} className="animate-spin text-blue-500 absolute right-3 top-1/2 -translate-y-1/2" />}
            </div>
            {open && suggestions.length > 0 && (
                <ul data-testid="acq-city-suggestions-list" className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-xl max-h-64 overflow-y-auto">
                    {suggestions.map((s, i) => (
                        <li key={i}>
                            <button
                                type="button"
                                data-testid={`acq-city-suggestion-${i}`}
                                onClick={() => pick(s)}
                                className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 flex items-start gap-2 transition-colors"
                            >
                                <MapPin size={14} className="text-blue-500 mt-0.5 shrink-0" />
                                <span className="truncate">{s.display_name || s.name}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
